import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  ArrowPathIcon,
  BeakerIcon,
  PlayIcon,
  StopIcon,
  CloudIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import SpotSearchInput from "@/components/spotimages/SpotSearchInput";
import LocationPickerMap from "@/components/admin/LocationPickerMap";
import { SpotSearchResult } from "@/common/model/spotimages/SpotImage";
import {
  TestDataService,
  TestDataStatus,
  TestDataReadiness,
  SimRun,
  InjectMode,
  InjectSessionResponse,
  WindProfileType,
} from "@/services/TestDataService";

const STATUS_REFRESH_MS = 10_000;
// Keep in sync with Caps.MAX_INJECT_LIVE_MINUTES in the simulator.
const MAX_LIVE_MINUTES = 240;

type Message = { type: "success" | "error"; text: string };

function errorText(e: any, fallback: string): string {
  return e?.userMessage || e?.response?.data?.error || e?.message || fallback;
}

function is404(e: any): boolean {
  return e?.statusCode === 404 || e?.response?.status === 404;
}

function formatTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

const RUN_STATE_STYLES: Record<string, string> = {
  RUNNING: "bg-green-900/50 text-green-300",
  STOPPED: "bg-gray-700 text-gray-300",
  EXPIRED: "bg-amber-900/50 text-amber-300",
  ERROR: "bg-red-900/50 text-red-300",
};

const StatePill: React.FC<{ state: string }> = ({ state }) => (
  <span
    className={`px-2 py-0.5 rounded text-xs font-semibold ${
      RUN_STATE_STYLES[state] || "bg-gray-700 text-gray-300"
    }`}
  >
    {state}
  </span>
);

type ChipState = "green" | "amber" | "red";

const READINESS_CHIP_STYLES: Record<ChipState, string> = {
  green: "bg-green-900/50 text-green-300 border-green-700/50",
  amber: "bg-amber-900/50 text-amber-300 border-amber-700/50",
  red: "bg-red-900/50 text-red-300 border-red-700/50",
};

const ReadinessChip: React.FC<{ label: string; state: ChipState; detail?: string }> = ({
  label,
  state,
  detail,
}) => (
  <div className={`rounded-lg border px-3 py-2 ${READINESS_CHIP_STYLES[state]}`}>
    <div className="text-sm font-semibold">{label}</div>
    {detail && <div className="text-xs opacity-80 mt-0.5">{detail}</div>}
  </div>
);

const SectionCard: React.FC<{
  title: string;
  icon?: React.ReactNode;
  actions?: React.ReactNode;
  children: React.ReactNode;
}> = ({ title, icon, actions, children }) => (
  <div className="bg-gray-800 rounded-lg p-4 sm:p-6">
    <div className="flex items-center justify-between gap-4 mb-4">
      <h2 className="flex items-center gap-2 text-lg font-semibold text-white">
        {icon}
        {title}
      </h2>
      {actions}
    </div>
    {children}
  </div>
);

const inputClass =
  "w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500";
const labelClass = "block text-xs uppercase tracking-wide text-gray-400 mb-1";
const primaryButtonClass =
  "flex items-center gap-2 px-4 py-2 bg-blue-600 rounded-lg text-white text-sm font-medium hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed";
const secondaryButtonClass =
  "flex items-center gap-2 px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed";
const dangerButtonClass =
  "flex items-center gap-2 px-4 py-2 border border-red-500 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/10 disabled:opacity-50 disabled:cursor-not-allowed";

const TestDataPage: React.FC = () => {
  const [status, setStatus] = useState<TestDataStatus | null>(null);
  const [readiness, setReadiness] = useState<TestDataReadiness | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  const [message, setMessage] = useState<Message | null>(null);
  const messageTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Scenario control
  const [selectedScenario, setSelectedScenario] = useState("");
  const [starting, setStarting] = useState(false);
  const [stoppingAll, setStoppingAll] = useState(false);

  // Readiness
  const [readinessLoading, setReadinessLoading] = useState(false);

  // Inject session form
  const [injectSpot, setInjectSpot] = useState<SpotSearchResult | null>(null);
  const [persona, setPersona] = useState("");
  const [injectMode, setInjectMode] = useState<InjectMode>("saved");
  const [duration, setDuration] = useState(90);
  // "" = auto (pick from wind); otherwise a fixed 8–12 m kite size.
  const [kiteSize, setKiteSize] = useState<"" | 8 | 9 | 10 | 11 | 12>("");
  const [injecting, setInjecting] = useState(false);
  const [injectResult, setInjectResult] = useState<InjectSessionResponse | null>(null);
  const [stoppingRun, setStoppingRun] = useState(false);

  // Synthetic wind form
  const [windName, setWindName] = useState("");
  const [windLat, setWindLat] = useState("");
  const [windLon, setWindLon] = useState("");
  const [windProfile, setWindProfile] = useState<WindProfileType>("constant");
  const [windSpeed, setWindSpeed] = useState(18);
  const [windDirection, setWindDirection] = useState(270);
  const [windHours, setWindHours] = useState(24);
  const [creatingWind, setCreatingWind] = useState(false);
  const [removingWind, setRemovingWind] = useState(false);

  const showMessage = useCallback((msg: Message) => {
    setMessage(msg);
    if (messageTimer.current) clearTimeout(messageTimer.current);
    messageTimer.current = setTimeout(() => setMessage(null), 5000);
  }, []);

  useEffect(() => {
    return () => {
      if (messageTimer.current) clearTimeout(messageTimer.current);
    };
  }, []);

  const loadStatus = useCallback(async (): Promise<boolean> => {
    try {
      const data = await TestDataService.fetchStatus();
      setStatus(data);
      setUnavailable(false);
      setSelectedScenario((prev) =>
        prev && data.scenarios.includes(prev) ? prev : data.scenarios[0] || ""
      );
      return true;
    } catch (e: any) {
      if (is404(e)) {
        setUnavailable(true);
      }
      return false;
    }
  }, []);

  const loadReadiness = useCallback(async () => {
    setReadinessLoading(true);
    try {
      setReadiness(await TestDataService.fetchReadiness());
    } catch (e: any) {
      if (is404(e)) setUnavailable(true);
      else showMessage({ type: "error", text: errorText(e, "Failed to load readiness") });
    } finally {
      setReadinessLoading(false);
    }
  }, [showMessage]);

  // Initial load + 10 s status auto-refresh while the page is open.
  useEffect(() => {
    loadStatus();
    loadReadiness();
    const interval = setInterval(() => {
      loadStatus();
    }, STATUS_REFRESH_MS);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStart = async () => {
    if (!selectedScenario) return;
    setStarting(true);
    try {
      const res = await TestDataService.startScenario(selectedScenario);
      showMessage({
        type: "success",
        text: `Started scenario "${selectedScenario}" (run ${res.runId}).`,
      });
      loadStatus();
    } catch (e: any) {
      if (is404(e)) setUnavailable(true);
      else showMessage({ type: "error", text: errorText(e, "Failed to start scenario") });
    } finally {
      setStarting(false);
    }
  };

  const handleStopAll = async () => {
    setStoppingAll(true);
    try {
      const res = await TestDataService.stopAll();
      showMessage({ type: "success", text: `Stopped ${res.stopped} run(s).` });
      loadStatus();
    } catch (e: any) {
      if (is404(e)) setUnavailable(true);
      else showMessage({ type: "error", text: errorText(e, "Failed to stop runs") });
    } finally {
      setStoppingAll(false);
    }
  };

  const handleInjectSpotSelect = useCallback((spot: SpotSearchResult) => {
    setInjectSpot(spot);
    // Default synthetic-station coords to the selected spot + a small offset.
    // Some spots have no coordinates on record — skip the default rather than
    // writing "NaN" into the form.
    if (typeof spot.latitude === "number" && typeof spot.longitude === "number") {
      setWindLat((prev) => (prev === "" ? (spot.latitude + 0.01).toFixed(5) : prev));
      setWindLon((prev) => (prev === "" ? (spot.longitude + 0.01).toFixed(5) : prev));
    }
  }, []);

  const injectSpotHasCoords =
    !!injectSpot &&
    typeof injectSpot.latitude === "number" &&
    typeof injectSpot.longitude === "number";
  // Persona may be empty when the dropdown is available ("Any" -> backend uses the
  // QA persona). With the free-text fallback there's no default, so require a value.
  const personaOk = (status?.personas?.length ?? 0) > 0 || persona.trim().length > 0;
  const canInject = injectSpotHasCoords && personaOk;

  const handleInject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canInject || injecting || !injectSpot || !injectSpotHasCoords) return;
    setInjecting(true);
    setInjectResult(null);
    try {
      const res = await TestDataService.injectSession({
        spotId: injectSpot.id,
        lat: injectSpot.latitude,
        lon: injectSpot.longitude,
        persona: persona.trim(),
        mode: injectMode,
        durationMinutes: injectMode === "live" ? duration : undefined,
        kiteSize: kiteSize === "" ? undefined : kiteSize,
      });
      setInjectResult(res);
      showMessage({
        type: "success",
        text: `Injected ${injectMode} session at ${injectSpot.name}.`,
      });
      loadStatus();
    } catch (e: any) {
      if (is404(e)) setUnavailable(true);
      else showMessage({ type: "error", text: errorText(e, "Failed to inject session") });
    } finally {
      setInjecting(false);
    }
  };

  const handleStopInjectedRun = async (runId: string) => {
    setStoppingRun(true);
    try {
      await TestDataService.stopInjectedRun(runId);
      showMessage({ type: "success", text: `Stopped run ${runId}.` });
      setInjectResult(null);
      loadStatus();
    } catch (e: any) {
      if (is404(e)) setUnavailable(true);
      else showMessage({ type: "error", text: errorText(e, "Failed to stop run") });
    } finally {
      setStoppingRun(false);
    }
  };

  const canCreateWind =
    windName.trim().length > 0 &&
    windLat !== "" &&
    windLon !== "" &&
    Number.isFinite(Number(windLat)) &&
    Number.isFinite(Number(windLon)) &&
    windHours >= 1 &&
    windHours <= 48;

  const handleCreateWind = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canCreateWind || creatingWind) return;
    setCreatingWind(true);
    try {
      const res = await TestDataService.createSyntheticWind({
        name: windName.trim(),
        lat: Number(windLat),
        lon: Number(windLon),
        profile: windProfile,
        speedKnots: windSpeed,
        directionDeg: windDirection,
        hours: windHours,
      });
      showMessage({
        type: "success",
        text: `Created synthetic station "${res.name || windName.trim()}"${
          res.stationId !== undefined ? ` (id ${res.stationId})` : ""
        }.`,
      });
    } catch (e: any) {
      if (is404(e)) setUnavailable(true);
      else
        showMessage({ type: "error", text: errorText(e, "Failed to create synthetic station") });
    } finally {
      setCreatingWind(false);
    }
  };

  const handleRemoveAllWind = async () => {
    if (
      !window.confirm(
        "Remove ALL synthetic wind stations? Their profiles stop immediately and the stations are deleted."
      )
    ) {
      return;
    }
    setRemovingWind(true);
    try {
      const res = await TestDataService.removeAllSyntheticWind();
      showMessage({ type: "success", text: `Removed ${res.removed} synthetic station(s).` });
    } catch (e: any) {
      if (is404(e)) setUnavailable(true);
      else
        showMessage({ type: "error", text: errorText(e, "Failed to remove synthetic stations") });
    } finally {
      setRemovingWind(false);
    }
  };

  if (unavailable) {
    return (
      <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto mt-12 bg-gray-800 rounded-lg p-8 text-center">
          <BeakerIcon className="w-10 h-10 text-amber-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Test environment only</h1>
          <p className="text-gray-400 text-sm">
            The test-data simulator endpoints are not available on this backend. They only exist
            when the backend runs with <code className="text-gray-300">STAGE=TEST</code> — this
            page cannot do anything against production.
          </p>
          <button
            onClick={() => {
              setUnavailable(false);
              loadStatus();
              loadReadiness();
            }}
            className={`${secondaryButtonClass} mx-auto mt-6`}
          >
            <ArrowPathIcon className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const MAX_RUNS_SHOWN = 10;
  const allRuns: SimRun[] = status?.runs || [];
  // Active runs first, then most-recently-started; show at most 10 (the rest scroll
  // out of the backend's recentRuns window anyway — this is a live ops view, not a log).
  const sortedRuns = [...allRuns].sort((a, b) => {
    const aActive = a.state === "ACTIVE" ? 0 : 1;
    const bActive = b.state === "ACTIVE" ? 0 : 1;
    if (aActive !== bActive) return aActive - bActive;
    return (b.startedAt || "").localeCompare(a.startedAt || "");
  });
  const runs = sortedRuns.slice(0, MAX_RUNS_SHOWN);
  const hiddenRunCount = sortedRuns.length - runs.length;
  const activeWind = status?.windProfiles || [];

  return (
    <div className="min-h-full bg-gray-900 py-6 px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Test Data</h1>
          <p className="text-gray-400 text-sm mt-1">
            Drive the dev simulator: scenarios, injected sessions and synthetic wind
            {status?.target ? ` — target: ${status.target}` : ""}
          </p>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div
          className={`mb-4 p-4 rounded-lg text-sm ${
            message.type === "success"
              ? "bg-green-900/50 text-green-300"
              : "bg-red-900/50 text-red-300"
          }`}
        >
          {message.text}
        </div>
      )}

      <div className="space-y-6">
        {/* 1. Scenario control */}
        <SectionCard
          title="Scenarios"
          icon={<PlayIcon className="w-5 h-5 text-blue-400" />}
          actions={
            <button onClick={() => loadStatus()} className={secondaryButtonClass}>
              <ArrowPathIcon className="w-4 h-4" />
              Refresh
            </button>
          }
        >
          <div className="flex flex-col sm:flex-row sm:items-end gap-3 mb-4">
            <div className="flex-1 max-w-xs">
              <label className={labelClass}>Scenario</label>
              <select
                value={selectedScenario}
                onChange={(e) => setSelectedScenario(e.target.value)}
                className={inputClass}
              >
                {(status?.scenarios || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                {(status?.scenarios || []).length === 0 && (
                  <option value="">No scenarios available</option>
                )}
              </select>
            </div>
            <button
              onClick={handleStart}
              disabled={!selectedScenario || starting}
              className={primaryButtonClass}
            >
              <PlayIcon className="w-4 h-4" />
              {starting ? "Starting..." : "Start"}
            </button>
            <button
              onClick={handleStopAll}
              disabled={stoppingAll || runs.length === 0}
              className={dangerButtonClass}
            >
              <StopIcon className="w-4 h-4" />
              {stoppingAll ? "Stopping..." : "Stop all"}
            </button>
          </div>

          {status?.caps && Object.keys(status.caps).length > 0 && (
            <div className="text-xs text-gray-500 mb-4">
              Caps:{" "}
              {Object.entries(status.caps)
                .map(([k, v]) => `${k}=${v}`)
                .join(" · ")}
            </div>
          )}

          {/* Runs table — capped at 10 rows, active first, scrolls beyond ~6 */}
          <div className="overflow-auto rounded-lg border border-gray-700 max-h-80">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wide sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Run</th>
                  <th className="px-4 py-2 text-left font-medium">Scenario</th>
                  <th className="px-4 py-2 text-left font-medium">State</th>
                  <th className="px-4 py-2 text-right font-medium">Live actors</th>
                  <th className="px-4 py-2 text-right font-medium">Ticks</th>
                  <th className="px-4 py-2 text-left font-medium">Started</th>
                  <th className="px-4 py-2 text-left font-medium">Last tick</th>
                  <th className="px-4 py-2 text-left font-medium">Expires</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700 text-gray-300">
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-center text-gray-500">
                      No runs — start a scenario above. Status refreshes every 10 s.
                    </td>
                  </tr>
                ) : (
                  runs.map((run) => (
                    <tr key={run.id} className="hover:bg-gray-700/40">
                      <td className="px-4 py-2 font-mono text-xs text-gray-400">{run.id}</td>
                      <td className="px-4 py-2 text-white">{run.scenario}</td>
                      <td className="px-4 py-2">
                        <StatePill state={run.state} />
                      </td>
                      <td className="px-4 py-2 text-right">{run.liveActors}</td>
                      <td className="px-4 py-2 text-right">{run.tickCount}</td>
                      <td className="px-4 py-2">{formatTime(run.startedAt)}</td>
                      <td className="px-4 py-2">{formatTime(run.lastTickAt)}</td>
                      <td className="px-4 py-2">{formatTime(run.expiresAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {hiddenRunCount > 0 && (
            <p className="mt-2 text-xs text-gray-500">
              Showing the {MAX_RUNS_SHOWN} most recent runs (active first).{" "}
              {hiddenRunCount} older run{hiddenRunCount === 1 ? "" : "s"} hidden.
            </p>
          )}
        </SectionCard>

        {/* 2. Readiness */}
        <SectionCard
          title="Dataset readiness"
          icon={<BeakerIcon className="w-5 h-5 text-cyan-400" />}
          actions={
            <button
              onClick={loadReadiness}
              disabled={readinessLoading}
              className={secondaryButtonClass}
            >
              <ArrowPathIcon className={`w-4 h-4 ${readinessLoading ? "animate-spin" : ""}`} />
              Refresh
            </button>
          }
        >
          {readiness ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <ReadinessChip
                label="Stations"
                state={readiness.stations.ok ? "green" : readiness.stations.count > 0 ? "amber" : "red"}
                detail={`${readiness.stations.count} station(s)`}
              />
              <ReadinessChip
                label="Forecast"
                state={readiness.forecast.ok ? "green" : "red"}
                detail={readiness.forecast.detail || (readiness.forecast.ok ? "fresh" : "stale/missing")}
              />
              <ReadinessChip
                label="Live friends"
                state={
                  readiness.liveFriends.ok ? "green" : readiness.liveFriends.count > 0 ? "amber" : "red"
                }
                detail={`${readiness.liveFriends.count} live friend(s)`}
              />
              <ReadinessChip
                label="QA jump today"
                state={readiness.qaJumpToday.ok ? "green" : "red"}
                detail={readiness.qaJumpToday.ok ? "present" : "missing"}
              />
            </div>
          ) : (
            <div className="text-sm text-gray-500 py-2">
              {readinessLoading ? "Loading readiness checks..." : "Readiness not loaded yet."}
            </div>
          )}
        </SectionCard>

        {/* 3. Inject session */}
        <SectionCard title="Inject session" icon={<PlayIcon className="w-5 h-5 text-green-400" />}>
          <form onSubmit={handleInject} className="space-y-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Spot</label>
                <SpotSearchInput onSpotSelect={handleInjectSpotSelect} selectedSpot={injectSpot} />
                {injectSpot && (
                  <div className="text-xs text-gray-400 mt-1">
                    Selected: <span className="text-white">{injectSpot.name}</span> (ID{" "}
                    {injectSpot.id}
                    {injectSpotHasCoords
                      ? `, ${injectSpot.latitude.toFixed(4)}, ${injectSpot.longitude.toFixed(4)}`
                      : " — coordinates unavailable"}
                    )
                    {!injectSpotHasCoords && (
                      <div className="text-amber-400 mt-1">
                        This spot has no coordinates on record — injection is disabled for it.
                        Pick a different spot.
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className={labelClass}>Persona</label>
                {status?.personas && status.personas.length > 0 ? (
                  <select
                    value={persona}
                    onChange={(e) => setPersona(e.target.value)}
                    className={inputClass}
                  >
                    <option value="">Any (QA persona)</option>
                    {status.personas.map((p) => (
                      <option key={p.name} value={p.name}>
                        {p.display} · {p.skill.toLowerCase()} ({p.name})
                      </option>
                    ))}
                  </select>
                ) : (
                  // Fallback for backends that don't yet expose the persona list.
                  <input
                    type="text"
                    value={persona}
                    onChange={(e) => setPersona(e.target.value)}
                    placeholder="sim-qa"
                    className={inputClass}
                  />
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div>
                <label className={labelClass}>Mode</label>
                <div className="flex items-center gap-4 py-2">
                  {(["saved", "live"] as InjectMode[]).map((m) => (
                    <label key={m} className="flex items-center gap-2 text-sm text-gray-300">
                      <input
                        type="radio"
                        name="inject-mode"
                        value={m}
                        checked={injectMode === m}
                        onChange={() => setInjectMode(m)}
                        className="accent-blue-500"
                      />
                      {m === "saved" ? "Saved session" : "Live session"}
                    </label>
                  ))}
                </div>
              </div>
              {injectMode === "live" && (
                <div className="w-40">
                  <label className={labelClass}>Duration (min, max {MAX_LIVE_MINUTES})</label>
                  <input
                    type="number"
                    min={1}
                    max={MAX_LIVE_MINUTES}
                    value={duration}
                    onChange={(e) =>
                      setDuration(Math.max(1, Math.min(MAX_LIVE_MINUTES, Number(e.target.value) || 1)))
                    }
                    className={inputClass}
                  />
                </div>
              )}
              <div className="w-44">
                <label className={labelClass}>Kite size</label>
                <select
                  value={kiteSize}
                  onChange={(e) =>
                    setKiteSize(e.target.value === "" ? "" : (Number(e.target.value) as 8 | 9 | 10 | 11 | 12))
                  }
                  className={inputClass}
                  title="Kite size in metres. Auto picks from the spot's current wind."
                >
                  <option value="">Auto (from wind)</option>
                  {[8, 9, 10, 11, 12].map((m) => (
                    <option key={m} value={m}>
                      {m} m
                    </option>
                  ))}
                </select>
              </div>
              <button type="submit" disabled={!canInject || injecting} className={primaryButtonClass}>
                {injecting ? "Injecting..." : "Inject session"}
              </button>
            </div>
          </form>

          {injectResult && (
            <div className="mt-4 p-3 bg-gray-900 border border-gray-700 rounded-lg flex flex-col sm:flex-row sm:items-center gap-3 text-sm">
              <div className="flex-1 text-gray-300">
                {injectResult.sessionId !== undefined && (
                  <span>
                    Session ID: <span className="font-mono text-white">{injectResult.sessionId}</span>
                  </span>
                )}
                {injectResult.sessionId !== undefined && injectResult.runId && <span> · </span>}
                {injectResult.runId && (
                  <span>
                    Run ID: <span className="font-mono text-white">{injectResult.runId}</span>
                  </span>
                )}
                {injectResult.sessionId === undefined && !injectResult.runId && (
                  <span>Session injected.</span>
                )}
              </div>
              {injectResult.runId && (
                <button
                  onClick={() => handleStopInjectedRun(injectResult.runId!)}
                  disabled={stoppingRun}
                  className={dangerButtonClass}
                >
                  <StopIcon className="w-4 h-4" />
                  {stoppingRun ? "Stopping..." : "Stop run"}
                </button>
              )}
            </div>
          )}
        </SectionCard>

        {/* 4. Synthetic wind */}
        <SectionCard
          title="Synthetic wind"
          icon={<CloudIcon className="w-5 h-5 text-purple-400" />}
          actions={
            <button onClick={handleRemoveAllWind} disabled={removingWind} className={dangerButtonClass}>
              <TrashIcon className="w-4 h-4" />
              {removingWind ? "Removing..." : "Remove all synthetic"}
            </button>
          }
        >
          <form onSubmit={handleCreateWind} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="lg:col-span-2">
                <label className={labelClass}>Station name</label>
                <input
                  type="text"
                  value={windName}
                  onChange={(e) => setWindName(e.target.value)}
                  placeholder="QA synthetic station"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Latitude</label>
                <input
                  type="number"
                  step="any"
                  value={windLat}
                  onChange={(e) => setWindLat(e.target.value)}
                  placeholder="52.37"
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Longitude</label>
                <input
                  type="number"
                  step="any"
                  value={windLon}
                  onChange={(e) => setWindLon(e.target.value)}
                  placeholder="4.89"
                  className={inputClass}
                />
              </div>
              <div className="sm:col-span-2 lg:col-span-4">
                <label className={labelClass}>Pick location</label>
                <LocationPickerMap
                  lat={windLat === "" ? null : Number(windLat)}
                  lon={windLon === "" ? null : Number(windLon)}
                  onPick={(la, lo) => {
                    setWindLat(String(la));
                    setWindLon(String(lo));
                  }}
                  heightPx={280}
                />
              </div>
              <div>
                <label className={labelClass}>Profile</label>
                <select
                  value={windProfile}
                  onChange={(e) => setWindProfile(e.target.value as WindProfileType)}
                  className={inputClass}
                >
                  <option value="constant">Constant</option>
                  <option value="ramp">Ramp</option>
                  <option value="oscillate">Oscillate</option>
                </select>
              </div>
              <div>
                <label className={labelClass}>Speed (kn)</label>
                <input
                  type="number"
                  min={0}
                  max={80}
                  value={windSpeed}
                  onChange={(e) => setWindSpeed(Math.max(0, Number(e.target.value) || 0))}
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Direction (°)</label>
                <input
                  type="number"
                  min={0}
                  max={360}
                  value={windDirection}
                  onChange={(e) =>
                    setWindDirection(Math.max(0, Math.min(360, Number(e.target.value) || 0)))
                  }
                  className={inputClass}
                />
              </div>
              <div>
                <label className={labelClass}>Hours (max 48)</label>
                <input
                  type="number"
                  min={1}
                  max={48}
                  value={windHours}
                  onChange={(e) =>
                    setWindHours(Math.max(1, Math.min(48, Number(e.target.value) || 1)))
                  }
                  className={inputClass}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="submit" disabled={!canCreateWind || creatingWind} className={primaryButtonClass}>
                <CloudIcon className="w-4 h-4" />
                {creatingWind ? "Creating..." : "Create station"}
              </button>
              <span className="text-xs text-gray-500">
                Max 10 synthetic stations; profiles auto-expire after 48 h. Stopping a profile lets
                the station go stale (&gt;1 h) for staleness testing.
              </span>
            </div>
          </form>

          {/* Active synthetic stations — the running live wind generation */}
          <div className="mt-6">
            <h3 className="text-sm font-medium text-gray-300 mb-2">
              Active synthetic stations
              {activeWind.length > 0 && (
                <span className="ml-2 text-xs text-gray-500">
                  ({activeWind.length} running · updates every 10 s)
                </span>
              )}
            </h3>
            <div className="overflow-x-auto rounded-lg border border-gray-700">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-900/60 text-gray-400 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium">Station</th>
                    <th className="px-4 py-2 text-left font-medium">Profile</th>
                    <th className="px-4 py-2 text-right font-medium">Wind now</th>
                    <th className="px-4 py-2 text-right font-medium">Gust now</th>
                    <th className="px-4 py-2 text-right font-medium">Dir</th>
                    <th className="px-4 py-2 text-left font-medium">Coords</th>
                    <th className="px-4 py-2 text-left font-medium">Expires</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700 text-gray-300">
                  {activeWind.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-6 text-center text-gray-500">
                        No synthetic stations running. Create one above to generate live wind.
                      </td>
                    </tr>
                  ) : (
                    activeWind.map((w) => (
                      <tr key={w.stationId} className="hover:bg-gray-700/40">
                        <td className="px-4 py-2">
                          <div className="text-white">{w.name}</div>
                          <div className="font-mono text-xs text-gray-500">{w.stationId}</div>
                        </td>
                        <td className="px-4 py-2">
                          <span className="inline-block rounded bg-purple-500/20 text-purple-300 px-2 py-0.5 text-xs">
                            {w.type.toLowerCase()}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-white">
                          {w.currentSpeedKn.toFixed(1)} kt
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-400">
                          {w.currentGustKn.toFixed(1)}
                        </td>
                        <td className="px-4 py-2 text-right tabular-nums text-gray-400">
                          {Math.round(w.directionDeg)}°
                        </td>
                        <td className="px-4 py-2 text-gray-400 tabular-nums">
                          {w.lat.toFixed(3)}, {w.lon.toFixed(3)}
                        </td>
                        <td className="px-4 py-2 text-gray-400">{formatTime(w.expiresAt)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Live values recompute from each profile every status refresh — watch a{" "}
              <span className="text-gray-300">ramp</span> or{" "}
              <span className="text-gray-300">oscillate</span> station change here. Full station
              management is under <span className="text-gray-300">Wind → Stations</span>.
            </p>
          </div>
        </SectionCard>
      </div>
    </div>
  );
};

export default TestDataPage;
